import { query } from "../../erd-editor-schema/dist/query/index.js";
import { getVisibleIds } from "../../konva/scene/viewLayout.js";
import { tableToObjectPoint } from "./calc.js";
import { union } from "./incremental.js";
import { isHorizontal, outwardSign } from "./stub.js";
import { searchRoute } from "./search.js";
//#region src/utils/draw-relationship/route.ts
var ROUTE_CLEARANCE = 16;
var OBSTACLE_INSET = 2;
var BEND_COST = 36;
var BLOCK_COST = 1e5;
var MAX_CHANNELS = 7;
var MAX_MIXED = 4;
var BACKTRACK_ALLOWANCE = 3;
function collectObstacles(state, source = "document") {
	const { tableIds } = getVisibleIds(state, source);
	const tables = query(state.collections).collection("tableEntities").selectByIds(tableIds);
	const size = tables.length;
	const obstacles = {
		ids: new Array(size),
		left: new Float64Array(size),
		top: new Float64Array(size),
		right: new Float64Array(size),
		bottom: new Float64Array(size)
	};
	tables.forEach((table, index) => {
		const point = tableToObjectPoint(state, table, source);
		obstacles.ids[index] = table.id;
		obstacles.left[index] = point.lt.x + OBSTACLE_INSET;
		obstacles.top[index] = point.lt.y + OBSTACLE_INSET;
		obstacles.right[index] = point.rb.x - OBSTACLE_INSET;
		obstacles.bottom[index] = point.rb.y - OBSTACLE_INSET;
	});
	return obstacles;
}
function segmentHitsBox(ax, ay, bx, by, left, top, right, bottom) {
	let enter = 0;
	let exit = 1;
	const dx = bx - ax;
	const dy = by - ay;
	const clip = (p, q) => {
		if (p === 0) return q >= 0;
		const t = q / p;
		if (p < 0) {
			if (t > exit) return false;
			if (t > enter) enter = t;
		} else {
			if (t < enter) return false;
			if (t < exit) exit = t;
		}
		return true;
	};
	return clip(-dx, ax - left) && clip(dx, right - ax) && clip(-dy, ay - top) && clip(dy, bottom - ay) && enter <= exit;
}
function countBlocked(points, obstacles, skipA, skipB) {
	let count = 0;
	let boundsLeft = Infinity;
	let boundsRight = -Infinity;
	let boundsTop = Infinity;
	let boundsBottom = -Infinity;
	for (let i = 0; i < points.length; i++) {
		const point = points[i];
		if (point.x < boundsLeft) boundsLeft = point.x;
		if (point.x > boundsRight) boundsRight = point.x;
		if (point.y < boundsTop) boundsTop = point.y;
		if (point.y > boundsBottom) boundsBottom = point.y;
	}
	for (let index = 0; index < obstacles.ids.length; index++) {
		const left = obstacles.left[index];
		const right = obstacles.right[index];
		const top = obstacles.top[index];
		const bottom = obstacles.bottom[index];
		if (boundsRight < left || boundsLeft > right) continue;
		if (boundsBottom < top || boundsTop > bottom) continue;
		const id = obstacles.ids[index];
		if (id === skipA || id === skipB) continue;
		for (let i = 1; i < points.length; i++) {
			const a = points[i - 1];
			const b = points[i];
			if (Math.max(a.x, b.x) < left || Math.min(a.x, b.x) > right) continue;
			if (Math.max(a.y, b.y) < top || Math.min(a.y, b.y) > bottom) continue;
			if (segmentHitsBox(a.x, a.y, b.x, b.y, left, top, right, bottom)) {
				count++;
				break;
			}
		}
	}
	return count;
}
function polylineLength(points) {
	let total = 0;
	for (let index = 1; index < points.length; index++) total += Math.abs(points[index].x - points[index - 1].x) + Math.abs(points[index].y - points[index - 1].y);
	return total;
}
function band(a, b) {
	return [Math.min(a, b) - ROUTE_CLEARANCE, Math.max(a, b) + ROUTE_CLEARANCE];
}
var anyLane = () => true;
function hasLane(lanes, lane) {
	for (let index = 0; index < lanes.length; index++) if (Math.abs(lanes[index] - lane) < .5) return true;
	return false;
}
function channels(from, to, obstacles, horizontal, [spanLow, spanHigh], limit, accept = anyLane) {
	const middle = (from + to) / 2;
	const low = obstacles[horizontal ? "left" : "top"];
	const high = obstacles[horizontal ? "right" : "bottom"];
	const crossLow = obstacles[horizontal ? "top" : "left"];
	const crossHigh = obstacles[horizontal ? "bottom" : "right"];
	const lanes = [];
	for (let index = 0; index < obstacles.ids.length; index++) {
		if (crossHigh[index] < spanLow || crossLow[index] > spanHigh) continue;
		lanes.push(low[index] - ROUTE_CLEARANCE, high[index] + ROUTE_CLEARANCE);
	}
	lanes.sort((a, b) => Math.abs(a - middle) - Math.abs(b - middle) || a - b);
	const tried = accept(middle) ? [middle] : [];
	for (const lane of lanes) {
		if (!accept(lane)) continue;
		if (hasLane(tried, lane)) continue;
		if (tried.length >= limit) return {
			lanes: tried,
			cut: Math.abs(tried[tried.length - 1] - middle)
		};
		tried.push(lane);
	}
	return {
		lanes: tried,
		cut: Infinity
	};
}
function sharedChannel(a, b, sign) {
	return (sign > 0 ? Math.max(a, b) : Math.min(a, b)) + sign * ROUTE_CLEARANCE;
}
function hidesStepOut(m, l, mSign) {
	const overlap = (m - l) * mSign;
	return overlap > 24 - ROUTE_CLEARANCE && overlap <= 48;
}
function stepOut(lanes, from, sign) {
	const lane = from + sign * ROUTE_CLEARANCE;
	if (!hasLane(lanes, lane)) lanes.push(lane);
}
function offersMiddle(mFrom, lTo, mSign, lFrom, mTo, lSign) {
	const back = ((mFrom + lTo) / 2 - mFrom) * mSign;
	const behind = ((lFrom + mTo) / 2 - lFrom) * lSign < 0;
	return back > -BACKTRACK_ALLOWANCE && (back < 0 || behind);
}
function candidates(m, mDirection, l, lDirection, obstacles) {
	const mH = isHorizontal(mDirection);
	const lH = isHorizontal(lDirection);
	const mSign = outwardSign(mDirection);
	const lSign = outwardSign(lDirection);
	const out = [];
	const push = (points) => {
		const cleaned = [points[0]];
		for (let index = 1; index < points.length; index++) {
			const previous = cleaned[cleaned.length - 1];
			const current = points[index];
			if (Math.abs(current.x - previous.x) < .5 && Math.abs(current.y - previous.y) < .5) continue;
			cleaned.push(current);
		}
		if (cleaned.length < 2) return;
		out.push({
			points: cleaned,
			bends: cleaned.length - 2
		});
	};
	let cutX = Infinity;
	let cutY = Infinity;
	if (mH && lH) {
		const xs = channels(m.x, l.x, obstacles, true, band(m.y, l.y), MAX_CHANNELS, (x) => (x - m.x) * mSign >= 0 && (x - l.x) * lSign >= 0);
		cutX = xs.cut;
		for (const x of xs.lanes) push([
			m,
			{
				x,
				y: m.y
			},
			{
				x,
				y: l.y
			},
			l
		]);
		if (mSign === lSign) {
			const x = sharedChannel(m.x, l.x, mSign);
			push([
				m,
				{
					x,
					y: m.y
				},
				{
					x,
					y: l.y
				},
				l
			]);
		} else if (hidesStepOut(m.x, l.x, mSign) && Math.abs(m.y - l.y) >= .5) {
			const x = (m.x + l.x) / 2;
			push([
				m,
				{
					x,
					y: m.y
				},
				{
					x,
					y: l.y
				},
				l
			]);
		}
		const a = m.x + mSign * ROUTE_CLEARANCE;
		const b = l.x + lSign * ROUTE_CLEARANCE;
		const ys = channels(m.y, l.y, obstacles, false, band(a, b), MAX_CHANNELS);
		cutY = ys.cut;
		for (const y of ys.lanes) push([
			m,
			{
				x: a,
				y: m.y
			},
			{
				x: a,
				y
			},
			{
				x: b,
				y
			},
			{
				x: b,
				y: l.y
			},
			l
		]);
	} else if (!mH && !lH) {
		const ys = channels(m.y, l.y, obstacles, false, band(m.x, l.x), MAX_CHANNELS, (y) => (y - m.y) * mSign >= 0 && (y - l.y) * lSign >= 0);
		cutY = ys.cut;
		for (const y of ys.lanes) push([
			m,
			{
				x: m.x,
				y
			},
			{
				x: l.x,
				y
			},
			l
		]);
		if (mSign === lSign) {
			const y = sharedChannel(m.y, l.y, mSign);
			push([
				m,
				{
					x: m.x,
					y
				},
				{
					x: l.x,
					y
				},
				l
			]);
		} else if (hidesStepOut(m.y, l.y, mSign) && Math.abs(m.x - l.x) >= .5) {
			const y = (m.y + l.y) / 2;
			push([
				m,
				{
					x: m.x,
					y
				},
				{
					x: l.x,
					y
				},
				l
			]);
		}
		const a = m.y + mSign * ROUTE_CLEARANCE;
		const b = l.y + lSign * ROUTE_CLEARANCE;
		const xs = channels(m.x, l.x, obstacles, true, band(a, b), MAX_CHANNELS);
		cutX = xs.cut;
		for (const x of xs.lanes) push([
			m,
			{
				x: m.x,
				y: a
			},
			{
				x,
				y: a
			},
			{
				x,
				y: b
			},
			{
				x: l.x,
				y: b
			},
			l
		]);
	} else if (mH) {
		if ((l.x - m.x) * mSign >= 0 && (m.y - l.y) * lSign >= 0) push([
			m,
			{
				x: l.x,
				y: m.y
			},
			l
		]);
		const xs = channels(m.x, l.x, obstacles, true, band(m.y, l.y), MAX_MIXED, (x) => (x - m.x) * mSign >= 0);
		const ys = channels(m.y, l.y, obstacles, false, band(m.x, l.x), MAX_MIXED, (y) => (y - l.y) * lSign >= 0);
		cutX = xs.cut;
		cutY = ys.cut;
		if (offersMiddle(m.x, l.x, mSign, l.y, m.y, lSign)) {
			const middle = (m.x + l.x) / 2;
			push([
				m,
				{
					x: middle,
					y: m.y
				},
				{
					x: middle,
					y: l.y
				},
				l
			]);
		}
		stepOut(xs.lanes, m.x, mSign);
		stepOut(ys.lanes, l.y, lSign);
		for (const x of xs.lanes) for (const y of ys.lanes) push([
			m,
			{
				x,
				y: m.y
			},
			{
				x,
				y
			},
			{
				x: l.x,
				y
			},
			l
		]);
	} else {
		if ((l.y - m.y) * mSign >= 0 && (m.x - l.x) * lSign >= 0) push([
			m,
			{
				x: m.x,
				y: l.y
			},
			l
		]);
		const ys = channels(m.y, l.y, obstacles, false, band(m.x, l.x), MAX_MIXED, (y) => (y - m.y) * mSign >= 0);
		const xs = channels(m.x, l.x, obstacles, true, band(m.y, l.y), MAX_MIXED, (x) => (x - l.x) * lSign >= 0);
		cutX = xs.cut;
		cutY = ys.cut;
		if (offersMiddle(m.y, l.y, mSign, l.x, m.x, lSign)) {
			const middle = (m.y + l.y) / 2;
			push([
				m,
				{
					x: m.x,
					y: middle
				},
				{
					x: l.x,
					y: middle
				},
				l
			]);
		}
		stepOut(ys.lanes, m.y, mSign);
		stepOut(xs.lanes, l.x, lSign);
		for (const y of ys.lanes) for (const x of xs.lanes) push([
			m,
			{
				x: m.x,
				y
			},
			{
				x,
				y
			},
			{
				x,
				y: l.y
			},
			l
		]);
	}
	return {
		options: out,
		cutX,
		cutY
	};
}
function reachOf(m, l, cost, cutX, cutY) {
	const spreadX = Math.min(cutX, (cost - Math.abs(m.y - l.y)) / 2);
	const spreadY = Math.min(cutY, (cost - Math.abs(m.x - l.x)) / 2);
	const middleX = (m.x + l.x) / 2;
	const middleY = (m.y + l.y) / 2;
	return {
		left: Math.min(m.x, l.x, middleX - spreadX),
		top: Math.min(m.y, l.y, middleY - spreadY),
		right: Math.max(m.x, l.x, middleX + spreadX),
		bottom: Math.max(m.y, l.y, middleY + spreadY)
	};
}
function routeWithReach(m, mDirection, l, lDirection, obstacles, skipA, skipB) {
	const { options, cutX, cutY } = candidates(m, mDirection, l, lDirection, obstacles);
	let best = null;
	let bestCost = Infinity;
	for (const option of options) {
		const cost = countBlocked(option.points, obstacles, skipA, skipB) * BLOCK_COST + option.bends * BEND_COST + polylineLength(option.points);
		if (cost < bestCost) {
			bestCost = cost;
			best = option.points;
		}
	}
	const reach = reachOf(m, l, bestCost, cutX, cutY);
	if (best && bestCost < BLOCK_COST) return {
		points: best,
		reach
	};
	const { points, region } = searchRoute(m, mDirection, l, lDirection, obstacles, skipA, skipB, ROUTE_CLEARANCE, BEND_COST);
	return {
		points: points ?? best ?? (isHorizontal(mDirection) ? [
			m,
			{
				x: (m.x + l.x) / 2,
				y: m.y
			},
			{
				x: (m.x + l.x) / 2,
				y: l.y
			},
			l
		] : [
			m,
			{
				x: m.x,
				y: (m.y + l.y) / 2
			},
			{
				x: l.x,
				y: (m.y + l.y) / 2
			},
			l
		]),
		reach: union(reach, region)
	};
}
//#endregion
export { collectObstacles, countBlocked, routeWithReach, segmentHitsBox };
